import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth.jsx';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Calendar, MapPin, CreditCard, Truck, Clock, CheckCircle, XCircle, AlertCircle, Smartphone, User, Download } from 'lucide-react';
import jsPDF from 'jspdf';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%);
  padding: 6rem 1.5rem 3rem;
  color: white;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: white;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h2 {
    font-size: 1.3rem;
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  
  &.delivered {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }
`;

const OrderInfo = styled.div`
  display: grid;
  gap: 1rem;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    
    .label {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.9rem;
    }
    
    .value {
      font-weight: 600;
      color: white;
    }
  }
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  margin-bottom: 1rem;
`;

const ProductIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  color: white;
  font-size: 1.8rem;
  flex-shrink: 0;
`;

const ProductDetails = styled.div`
  flex: 1;
  
  .name {
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  .config {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .price {
    color: #22c55e;
    font-weight: 700;
    font-size: 1.1rem;
  }
  
  .quantity {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const OrderDetailPageFixed = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      setLoading(true);
      
      // Simuler un appel API
      setTimeout(() => {
        const mockOrder = {
          id: orderId,
          date: '2024-12-10',
          status: 'delivered',
          total: 918000,
          subtotal: 918000,
          shipping: 0,
          items: [
            {
              id: 1,
              name: 'iPhone 15 Pro Max',
              config: '256GB - Bleu Titane',
              quantity: 1,
              price: 918000
            }
          ],
          paymentMethod: 'Wave',
          shippingAddress: 'Abidjan, Cocody, Zone 4, Rue des Jardins',
          trackingNumber: 'WV123456789CI'
        };
        
        setOrder(mockOrder);
        setLoading(false);
      }, 1000);
    };
    
    if (isAuthenticated && orderId) {
      loadOrder();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, orderId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'processing': return 'En traitement';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  const handleDownloadInvoice = (order) => {
    // Créer un nouveau document PDF
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const leftColumn = margin;
    const rightColumn = pageWidth - margin - 80;
    let yPosition = margin;
    
    // En-tête avec logo et titre
    pdf.setFillColor(59, 130, 246);
    pdf.rect(0, 0, pageWidth, 60, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('INZA STORE', leftColumn, 35);
    
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'normal');
    pdf.text('FACTURE', pageWidth - margin, 35, { align: 'right' });
    
    // Informations de la commande
    pdf.setTextColor(0, 0, 0);
    yPosition = 80;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Facture #: ${order.id}`, leftColumn, yPosition);
    yPosition += 6;
    pdf.text(`Date: ${formatDate(order.date)}`, leftColumn, yPosition);
    yPosition += 6;
    pdf.text(`Statut: ${getStatusText(order.status)}`, leftColumn, yPosition);
    
    // Informations client (colonne gauche)
    yPosition += 15;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CLIENT', leftColumn, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Utilisateur INZA', leftColumn, yPosition);
    yPosition += 5;
    pdf.text('user@inza.com', leftColumn, yPosition);
    yPosition += 5;
    pdf.text('+225 07 00 00 00 00', leftColumn, yPosition);
    yPosition += 5;
    // Gérer les adresses longues
    const addressLines = pdf.splitTextToSize(order.shippingAddress, 80);
    addressLines.forEach(line => {
      pdf.text(line, leftColumn, yPosition);
      yPosition += 5;
    });
    
    // Informations vendeur (colonne droite)
    let sellerY = 80;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('VENDEUR', rightColumn, sellerY);
    sellerY += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('INZA STORE', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('contact@inza.com', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('+225 07 00 00 00 00', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('Abidjan, Cocody', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('RC: CI-ABJ-2024-1234', rightColumn, sellerY);
    
    // Ligne de séparation
    yPosition += 15;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
    
    // En-tête du tableau des produits
    pdf.setFillColor(240, 240, 240);
    pdf.rect(leftColumn, yPosition, pageWidth - 2 * margin, 10, 'F');
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PRODUIT', leftColumn + 5, yPosition + 7);
    pdf.text('CONFIGURATION', leftColumn + 70, yPosition + 7);
    pdf.text('QUANTITÉ', leftColumn + 120, yPosition + 7);
    pdf.text('PRIX U.', leftColumn + 145, yPosition + 7);
    pdf.text('TOTAL', leftColumn + 175, yPosition + 7);
    
    yPosition += 15;
    
    // Tableau des produits
    order.items.forEach((item, index) => {
      if (yPosition > pageHeight - 80) {
        pdf.addPage();
        yPosition = margin;
        
        // Répéter l'en-tête du tableau sur la nouvelle page
        pdf.setFillColor(240, 240, 240);
        pdf.rect(leftColumn, yPosition, pageWidth - 2 * margin, 10, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('PRODUIT', leftColumn + 5, yPosition + 7);
        pdf.text('CONFIGURATION', leftColumn + 70, yPosition + 7);
        pdf.text('QUANTITÉ', leftColumn + 120, yPosition + 7);
        pdf.text('PRIX U.', leftColumn + 145, yPosition + 7);
        pdf.text('TOTAL', leftColumn + 175, yPosition + 7);
        yPosition += 15;
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      // Gérer les noms de produits longs
      const nameLines = pdf.splitTextToSize(item.name, 60);
      nameLines.forEach((line, lineIndex) => {
        pdf.text(line, leftColumn + 5, yPosition + (lineIndex * 4));
      });
      
      // Gérer les configurations longues
      const configLines = pdf.splitTextToSize(item.config, 45);
      configLines.forEach((line, lineIndex) => {
        pdf.text(line, leftColumn + 70, yPosition + (lineIndex * 4));
      });
      
      // Alignement parfait des colonnes numériques
      pdf.text(item.quantity.toString(), leftColumn + 130, yPosition, { align: 'center' });
      pdf.text(formatPrice(item.price), leftColumn + 160, yPosition, { align: 'right' });
      pdf.text(formatPrice(item.price * item.quantity), leftColumn + 190, yPosition, { align: 'right' });
      
      yPosition += Math.max(nameLines.length * 4, configLines.length * 4, 8);
      
      // Ligne de séparation entre les produits
      if (index < order.items.length - 1) {
        pdf.setDrawColor(230, 230, 230);
        pdf.line(leftColumn, yPosition - 2, pageWidth - margin, yPosition - 2);
      }
    });
    
    yPosition += 10;
    
    // Ligne de séparation avant le total
    pdf.setDrawColor(150, 150, 150);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    
    // Sous-total
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Sous-total:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(order.total), leftColumn + 190, yPosition, { align: 'right' });
    yPosition += 6;
    
    // Livraison
    pdf.text('Livraison:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(0), leftColumn + 190, yPosition, { align: 'right' });
    yPosition += 8;
    
    // Total en gras avec ligne
    pdf.setDrawColor(100, 100, 100);
    pdf.line(leftColumn + 135, yPosition - 2, pageWidth - margin, yPosition - 2);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('TOTAL:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(order.total), leftColumn + 190, yPosition, { align: 'right' });
    
    // Méthode de paiement
    yPosition += 12;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Méthode de paiement: ${order.paymentMethod}`, leftColumn, yPosition);
    
    // Pied de page
    yPosition = pageHeight - 40;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    
    yPosition += 10;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Merci pour votre confiance!', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text('INZA STORE - Votre boutique de produits Apple en Côte d\'Ivoire', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text('contact@inza.com | +225 07 00 00 00 00', pageWidth / 2, yPosition, { align: 'center' });
    
    // Téléchargement du PDF
    pdf.save(`Facture-${order.id}.pdf`);
    
    // Message de confirmation
    console.log(`Facture PDF stylisée générée pour la commande ${order.id}`);
  };

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <Package style={{ width: '4rem', height: '4rem', margin: '0 auto 2rem', color: 'rgba(255, 255, 255, 0.3)' }} />
            <h3>Connectez-vous pour voir les détails de votre commande</h3>
          </div>
        </Container>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#3b82f6', animation: 'spin 1s linear infinite' }}>
              <AlertCircle size={48} />
            </div>
            <h3>Chargement des détails de la commande...</h3>
          </div>
        </Container>
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <Package style={{ width: '4rem', height: '4rem', margin: '0 auto 2rem', color: 'rgba(255, 255, 255, 0.3)' }} />
            <h3>Commande non trouvée</h3>
            <BackButton onClick={() => navigate('/orders')}>
              <ArrowLeft size={16} />
              Retour aux commandes
            </BackButton>
          </div>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/orders')}>
            <ArrowLeft size={16} />
            Retour aux commandes
          </BackButton>
          <h1>Détails de la commande {order.id}</h1>
        </Header>

        <Card>
          <CardHeader>
            <h2>
              <Package size={20} />
              Informations de la commande
            </h2>
            <StatusBadge className={order.status}>
              <CheckCircle size={16} />
              Livrée
            </StatusBadge>
          </CardHeader>
          <OrderInfo>
            <div className="info-row">
              <span className="label">Numéro de commande</span>
              <span className="value">{order.id}</span>
            </div>
            <div className="info-row">
              <span className="label">Date de commande</span>
              <span className="value">{formatDate(order.date)}</span>
            </div>
            <div className="info-row">
              <span className="label">Méthode de paiement</span>
              <span className="value">{order.paymentMethod}</span>
            </div>
            <div className="info-row">
              <span className="label">Numéro de suivi</span>
              <span className="value">{order.trackingNumber}</span>
            </div>
            <div className="info-row">
              <span className="label">Adresse de livraison</span>
              <span className="value">{order.shippingAddress}</span>
            </div>
          </OrderInfo>
        </Card>

        <Card>
          <CardHeader>
            <h2>
              <Smartphone size={20} />
              Produits commandés
            </h2>
          </CardHeader>
          {order.items.map((item, index) => (
            <ProductItem key={index}>
              <ProductIcon>
                <Smartphone size={24} />
              </ProductIcon>
              <ProductDetails>
                <div className="name">{item.name}</div>
                <div className="config">{item.config}</div>
                <div className="price-row">
                  <span className="price">{formatPrice(item.price)}</span>
                  <span className="quantity">Quantité: {item.quantity}</span>
                </div>
              </ProductDetails>
            </ProductItem>
          ))}
        </Card>

        <Card>
          <CardHeader>
            <h2>
              <CreditCard size={20} />
              Récapitulatif
            </h2>
            <button
              onClick={() => handleDownloadInvoice(order)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#22c55e',
                cursor: 'pointer',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(34, 197, 94, 0.3)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(34, 197, 94, 0.2)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <Download size={16} />
              Télécharger la facture
            </button>
          </CardHeader>
          <OrderInfo>
            <div className="info-row">
              <span className="label">Sous-total</span>
              <span className="value">{formatPrice(order.subtotal)}</span>
            </div>
            <div className="info-row">
              <span className="label">Livraison</span>
              <span className="value">{formatPrice(order.shipping)}</span>
            </div>
            <div className="info-row">
              <span className="label">Total</span>
              <span className="value" style={{ color: '#22c55e', fontSize: '1.2rem' }}>
                {formatPrice(order.total)}
              </span>
            </div>
          </OrderInfo>
        </Card>
      </Container>
    </PageContainer>
  );
};

export default OrderDetailPageFixed;
